import { useCallback, useMemo, type MouseEvent } from 'react';
import { Typography, Box, CircularProgress } from '@material-ui/core';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import { LinePath, AreaClosed, Line } from '@visx/shape';
import { curveMonotoneX } from '@visx/curve';
import { scaleTime, scaleLinear } from '@visx/scale';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { useTooltip } from '@visx/tooltip';

import { bisector } from 'd3-array';
import { timeFormat } from 'd3-time-format';
import { ResponsiveChartWrapper } from './ResponsiveChartWrapper';
import { ChartTooltip } from './ChartTooltip';
import { seriesColor, CHART_THEME } from './chartTheme';
import { formatValue } from '../../utils/formatValue';
import type { TimeSeries, TimeSeriesPoint } from '../../api/types';

interface Props {
  series: TimeSeries[];
  unit: string;
  title?: string;
  height?: number;
  thresholdLine?: { value: number; color: string };
  loading?: boolean;
}

const margin = { top: 12, right: 16, bottom: 50, left: 60 };

const bisectTime = bisector<TimeSeriesPoint, number>(d => d.time).left;
const formatTime = timeFormat('%H:%M');
const formatTimeFull = timeFormat('%H:%M:%S');

const useStyles = makeStyles({
  legendTable: {
    width: '100%',
    fontSize: 11,
    borderCollapse: 'collapse',
    '& td, & th': {
      padding: '2px 8px',
      textAlign: 'right',
    },
    '& th': {
      fontWeight: 600,
      textAlign: 'left',
    },
  },
});

export const TimeSeriesChart = ({
  series,
  unit,
  height: propHeight = 300,
  thresholdLine,
  loading,
}: Props) => {
  const theme = useTheme();
  const classes = useStyles();
  const mode = theme.palette.type === 'dark' ? 'dark' : 'light';
  const colors = CHART_THEME[mode];

  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
  } = useTooltip<{ title: string; items: { color: string; label: string; value: string }[] }>();

  // Flatten all timestamps to determine x domain
  const allPoints = useMemo(
    () => series.flatMap(s => s.points),
    [series],
  );
  const xDomain = useMemo(() => {
    if (!allPoints.length) return [Date.now() - 3600000, Date.now()] as [number, number];
    return [
      Math.min(...allPoints.map(p => p.time)),
      Math.max(...allPoints.map(p => p.time)),
    ] as [number, number];
  }, [allPoints]);

  const yMax = useMemo(() => {
    const vals = allPoints.map(p => p.value).filter(Number.isFinite);
    if (!vals.length) return 1;
    const max = Math.max(...vals);
    return max === 0 ? 1 : max * 1.1;
  }, [allPoints]);

  const handleTooltip = useCallback(
    (event: MouseEvent<SVGRectElement>, _innerWidth: number, _innerHeight: number, xScale: any) => {
      const svg = (event.target as SVGElement).ownerSVGElement;
      if (!svg) return;
      const pt = svg.createSVGPoint();
      pt.x = event.clientX;
      pt.y = event.clientY;
      const point = pt.matrixTransform(svg.getScreenCTM()?.inverse());
      if (!point) return;

      const x0 = xScale.invert(point.x - margin.left);
      const items = series.map((s, i) => {
        const idx = bisectTime(s.points, x0.getTime(), 1);
        const d0 = s.points[idx - 1];
        const d1 = s.points[idx];
        const d = !d0 ? d1 : !d1 ? d0 : (x0.getTime() - d0.time > d1.time - x0.getTime() ? d1 : d0);
        return {
          color: seriesColor(i),
          label: s.label,
          value: d ? formatValue(d.value, unit) : 'N/A',
        };
      });

      showTooltip({
        tooltipLeft: point.x,
        tooltipTop: point.y,
        tooltipData: {
          title: formatTimeFull(x0),
          items,
        },
      });
    },
    [series, unit, showTooltip],
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={propHeight}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!series.length || !allPoints.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={propHeight}>
        <Typography variant="body2" style={{ opacity: 0.5 }}>No data</Typography>
      </Box>
    );
  }

  // Compute legend stats
  const legendData = series.map((s, i) => {
    const vals = s.points.map(p => p.value).filter(Number.isFinite);
    const mean = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    const max = vals.length ? Math.max(...vals) : null;
    const last = vals.length ? vals[vals.length - 1] : null;
    return { label: s.label, color: seriesColor(i), mean, max, last };
  });

  return (
    <Box position="relative">
      <ResponsiveChartWrapper height={propHeight}>
        {({ width, height }) => {
          const innerWidth = width - margin.left - margin.right;
          const innerHeight = height - margin.top - margin.bottom;

          const xScale = scaleTime({
            domain: xDomain,
            range: [0, innerWidth],
          });
          const yScale = scaleLinear({
            domain: [0, yMax],
            range: [innerHeight, 0],
            nice: true,
          });

          return (
            <svg width={width} height={height}>
              <Group left={margin.left} top={margin.top}>
                <GridRows
                  scale={yScale}
                  width={innerWidth}
                  stroke={colors.gridColor}
                  strokeDasharray="3,3"
                />

                {/* Area fills */}
                {series.map((s, i) => (
                  <AreaClosed
                    key={`area-${i}`}
                    data={s.points}
                    x={d => xScale(d.time)}
                    y={d => yScale(d.value)}
                    yScale={yScale}
                    curve={curveMonotoneX}
                    fill={seriesColor(i)}
                    fillOpacity={0.12}
                    strokeWidth={0}
                  />
                ))}

                {/* Lines */}
                {series.map((s, i) => (
                  <LinePath
                    key={`line-${i}`}
                    data={s.points}
                    x={d => xScale(d.time)}
                    y={d => yScale(d.value)}
                    curve={curveMonotoneX}
                    stroke={seriesColor(i)}
                    strokeWidth={2}
                  />
                ))}

                {/* Threshold line */}
                {thresholdLine && (
                  <Line
                    from={{ x: 0, y: yScale(thresholdLine.value) }}
                    to={{ x: innerWidth, y: yScale(thresholdLine.value) }}
                    stroke={thresholdLine.color}
                    strokeWidth={1.5}
                    strokeDasharray="6,4"
                    opacity={0.7}
                  />
                )}

                {/* Crosshair overlay */}
                {tooltipOpen && tooltipLeft !== undefined && (
                  <Line
                    from={{ x: (tooltipLeft ?? 0) - margin.left, y: 0 }}
                    to={{ x: (tooltipLeft ?? 0) - margin.left, y: innerHeight }}
                    stroke={colors.crosshairColor}
                    strokeWidth={1}
                    strokeDasharray="4,3"
                    pointerEvents="none"
                  />
                )}

                <AxisBottom
                  top={innerHeight}
                  scale={xScale}
                  tickFormat={d => formatTime(d as Date)}
                  stroke={colors.axisColor}
                  tickStroke={colors.axisColor}
                  tickLabelProps={() => ({
                    fill: colors.tickLabelColor,
                    fontSize: 10,
                    textAnchor: 'middle' as const,
                  })}
                  numTicks={Math.max(Math.floor(innerWidth / 80), 3)}
                />
                <AxisLeft
                  scale={yScale}
                  tickFormat={v => formatValue(v as number, unit)}
                  stroke={colors.axisColor}
                  tickStroke={colors.axisColor}
                  tickLabelProps={() => ({
                    fill: colors.tickLabelColor,
                    fontSize: 10,
                    textAnchor: 'end' as const,
                    dx: -4,
                  })}
                  numTicks={5}
                />

                {/* Invisible hover rect */}
                <rect
                  width={innerWidth}
                  height={innerHeight}
                  fill="transparent"
                  onMouseMove={e => handleTooltip(e, innerWidth, innerHeight, xScale)}
                  onMouseLeave={() => hideTooltip()}
                />
              </Group>
            </svg>
          );
        }}
      </ResponsiveChartWrapper>

      {/* Tooltip */}
      {tooltipOpen && tooltipData && (
        <ChartTooltip
          top={(tooltipTop ?? 0) - 10}
          left={(tooltipLeft ?? 0) + 10}
          data={tooltipData}
        />
      )}

      {/* Legend table */}
      <Box mt={1} px={1}>
        <table className={classes.legendTable}>
          <thead>
            <tr style={{ color: colors.tickLabelColor }}>
              <th style={{ width: '40%' }}>Series</th>
              <td>Mean</td>
              <td>Max</td>
              <td>Last</td>
            </tr>
          </thead>
          <tbody>
            {legendData.map((row, i) => (
              <tr key={i} style={{ color: colors.tooltipText }}>
                <th>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 8,
                      height: 3,
                      backgroundColor: row.color,
                      marginRight: 6,
                      borderRadius: 1,
                      verticalAlign: 'middle',
                    }}
                  />
                  {row.label}
                </th>
                <td>{formatValue(row.mean, unit)}</td>
                <td>{formatValue(row.max, unit)}</td>
                <td>{formatValue(row.last, unit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  );
};
